/**
 * Reglas de complejidad de contraseña de la app de Une Crédito.
 *
 * ⚠️ POLICY FIJA A PROPÓSITO — no la traigas del backend. `GET /public/password-policy` expone la
 * policy GLOBAL del lambda RBAC, que se bajó a `minLength: 6` sin clases para el autoregistro; el
 * pool Cognito de `une` exige 12 + las cuatro clases. Consultarla pinta un checklist más flojo que
 * Cognito y el guardado termina en "No se pudo actualizar la contraseña".
 *
 * Cuando el RBAC exponga policy per-tenant, esto vuelve a ser una query — validando que lo que
 * llega no sea MÁS FLOJO que el pool.
 */
export interface PasswordPolicy {
  minLength: number;
  requireSymbol: boolean;
  requireNumber: boolean;
  requireUppercase: boolean;
  requireLowercase: boolean;
}

/**
 * Policy del pool Cognito de `une`, verificada con `describe-user-pool`:
 * `MinimumLength: 12`, `RequireUppercase/Lowercase/Numbers/Symbols: true`.
 */
export const UNE_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 12,
  requireSymbol: true,
  requireNumber: true,
  requireUppercase: true,
  requireLowercase: true,
};

/**
 * Símbolos que Cognito acepta. El `-` va último (literal) y `^` / `[` / `]` / `\` van escapados.
 * Espejo de `COGNITO_SYMBOL` en `platform-rbac-business`.
 */
export const COGNITO_SYMBOL = /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`+=-]/;

/**
 * Charset completo que Cognito acepta: alfanumérico ASCII + los símbolos de arriba + espacio.
 * Cualquier otro carácter (acentos, `ñ`, emoji) lo rechaza con `InvalidPasswordException`.
 */
export const COGNITO_ALLOWED_CHARSET = /^[A-Za-z0-9 ^$*.[\]{}()?"!@#%&/\\,><':;|_~`+=-]+$/;

/** Regla individual del checklist en vivo. */
export interface PasswordRule {
  label: string;
  test: (v: string) => boolean;
}

/**
 * Arma el checklist en vivo: largo + las clases que la policy exige. El símbolo se valida contra el
 * set real de Cognito, no contra `[^A-Za-z0-9]` — con esa regex la `ñ` contaba como símbolo.
 */
export function buildPasswordRules(policy: PasswordPolicy): PasswordRule[] {
  const rules: PasswordRule[] = [
    {
      label: `Al menos ${String(policy.minLength)} caracteres`,
      test: (v) => v.length >= policy.minLength,
    },
  ];
  if (policy.requireUppercase) {
    rules.push({ label: 'Una mayúscula (A-Z)', test: (v) => /[A-Z]/.test(v) });
  }
  if (policy.requireLowercase) {
    rules.push({ label: 'Una minúscula (a-z)', test: (v) => /[a-z]/.test(v) });
  }
  if (policy.requireNumber) {
    rules.push({ label: 'Un número (0-9)', test: (v) => /[0-9]/.test(v) });
  }
  if (policy.requireSymbol) {
    rules.push({
      label: 'Un símbolo (! @ # $ % & * ? _ - + =)',
      test: (v) => COGNITO_SYMBOL.test(v),
    });
  }
  return rules;
}

/** `true` si el valor cumple TODAS las reglas de la policy. */
export function allRulesPass(rules: PasswordRule[], value: string): boolean {
  return rules.every((r) => r.test(value));
}

/**
 * Restricciones fijas de Cognito que no dependen de la policy: charset y espacios al borde.
 * Devuelve el motivo del rechazo, o `null` si la contraseña está limpia.
 *
 * No son ítems del checklist a propósito — serían dos palomitas verdes permanentes. Pero bloquean
 * el guardado: `Contraseña2026!` pasa las cinco reglas visibles y Cognito la rechaza igual.
 */
export function cognitoRejectionReason(value: string): string | null {
  if (value.length === 0) return null;
  if (!COGNITO_ALLOWED_CHARSET.test(value)) {
    return 'No puede llevar acentos, ñ ni emojis. Usa solo letras sin acento, números y símbolos.';
  }
  if (value !== value.trim()) {
    return 'No puede empezar ni terminar con espacio.';
  }
  return null;
}
